import type {
  AISuggestedAxis,
  AISuggestedChart,
  AISuggestedCurve,
  AxisQuantity,
  CurveDraftDocument,
  CurveKind,
  CurveTraceDraft,
} from './types';

const UNIT_ALIASES: Record<string, string> = {
  '%': 'percent',
  'bbl/min': 'bpm',
  bhp: 'hp',
  feet: 'ft',
  foot: 'ft',
  fraction: 'fraction',
  'gal/min': 'gpm',
  horsepower: 'hp',
  kw: 'kw',
  lps: 'l/s',
  meters: 'm',
  metres: 'm',
  'm^3/h': 'm3/h',
  'm^3/s': 'm3/s',
  'm³/h': 'm3/h',
  'm³/s': 'm3/s',
  pct: 'percent',
  'us gpm': 'gpm',
  usgpm: 'gpm',
  watts: 'w',
};

const SUPPORTED_UNITS: Record<AxisQuantity, ReadonlySet<string>> = {
  efficiency: new Set(['fraction', 'percent']),
  flow: new Set(['m3/s', 'm3/h', 'l/s', 'gpm', 'bpm']),
  head: new Set(['m', 'ft']),
  npshr: new Set(['m', 'ft']),
  power: new Set(['w', 'kw', 'hp']),
  suction_lift: new Set(['m', 'ft']),
};

const EXPECTED_Y_QUANTITY: Partial<Record<CurveKind, AxisQuantity>> = {
  brake_power: 'power',
  efficiency: 'efficiency',
  head: 'head',
  npshr: 'npshr',
  operating_boundary: 'head',
  suction_lift: 'suction_lift',
};

export interface AIApplyCompatibility {
  compatible: boolean;
  reasons: string[];
  xAxis: AISuggestedAxis | null;
  xUnit: string | null;
  yAxis: AISuggestedAxis | null;
  yUnit: string | null;
}

function axisRangeIsUsable(axis: AISuggestedAxis): boolean {
  return (
    axis.value_min !== null &&
    axis.value_max !== null &&
    Number.isFinite(axis.value_min) &&
    Number.isFinite(axis.value_max) &&
    axis.value_min < axis.value_max
  );
}

function within(value: number, axis: AISuggestedAxis): boolean {
  if (axis.value_min === null || axis.value_max === null) return false;
  return value >= axis.value_min && value <= axis.value_max;
}

export function normalizeCurveUnit(unit: string): string {
  const normalized = unit.trim().toLocaleLowerCase().replaceAll(/\s+/gu, ' ');
  return UNIT_ALIASES[normalized] ?? normalized;
}

function supportedUnit(quantity: AxisQuantity, unit: string): boolean {
  return SUPPORTED_UNITS[quantity].has(normalizeCurveUnit(unit));
}

function completeAxis(
  axis: AISuggestedAxis,
  quantity: AxisQuantity,
  axisName: string,
  reasons: string[],
): string | null {
  if (axis.quantity !== quantity) {
    reasons.push(`${axisName} must be identified as ${quantity.replaceAll('_', ' ')}.`);
  }
  if (axis.unit === null || axis.unit.trim() === '') {
    reasons.push(`${axisName} has no source unit.`);
    return null;
  }
  const normalizedUnit = normalizeCurveUnit(axis.unit);
  if (!supportedUnit(quantity, normalizedUnit)) {
    reasons.push(
      `${axisName} unit “${axis.unit}” is not supported for ${quantity.replaceAll('_', ' ')}.`,
    );
  }
  if (axis.scale === null || axis.direction === null || !axisRangeIsUsable(axis)) {
    reasons.push(
      `${axisName} needs an unambiguous scale, direction, minimum, and maximum.`,
    );
  }
  return normalizedUnit;
}

export function analyzeAiCurveCompatibility(
  chart: AISuggestedChart,
  curve: AISuggestedCurve,
  document: CurveDraftDocument,
  localRequestId: string,
): AIApplyCompatibility {
  const reasons: string[] = [];
  const expectedY = EXPECTED_Y_QUANTITY[curve.kind];
  if (expectedY === undefined) {
    reasons.push(
      'This curve family cannot be copied safely because the AI response does not carry the contour value and unit required by the draft contract.',
    );
  }

  const xAxis = chart.x_axis;
  const xUnit =
    xAxis === null ? null : completeAxis(xAxis, 'flow', 'Flow axis', reasons);
  if (xAxis === null) reasons.push('The chart has no identified flow axis.');

  const matchingYAxes =
    expectedY === undefined
      ? []
      : chart.y_axes.filter((axis) => axis.quantity === expectedY);
  const yAxis = matchingYAxes.length === 1 ? (matchingYAxes[0] ?? null) : null;
  if (expectedY !== undefined && matchingYAxes.length !== 1) {
    reasons.push(
      matchingYAxes.length === 0
        ? `No ${expectedY.replaceAll('_', ' ')} axis maps to this curve.`
        : `More than one ${expectedY.replaceAll('_', ' ')} axis could map to this curve.`,
    );
  }
  const yUnit =
    yAxis === null || expectedY === undefined
      ? null
      : completeAxis(yAxis, expectedY, 'Vertical axis', reasons);

  const calibration = document.calibration;
  if (calibration === null) {
    reasons.push('Create and save a manual axis calibration before copying source-unit points.');
  } else {
    if (
      xAxis !== null &&
      (calibration.x_axis.quantity !== 'flow' ||
        xUnit === null ||
        normalizeCurveUnit(calibration.x_axis.unit) !== xUnit ||
        calibration.x_axis.scale !== xAxis.scale ||
        calibration.x_axis.direction !== xAxis.direction)
    ) {
      reasons.push(
        'The saved flow calibration does not exactly match the AI axis quantity, unit, scale, and direction.',
      );
    }
    if (
      yAxis !== null &&
      expectedY !== undefined &&
      (calibration.y_axis.quantity !== expectedY ||
        yUnit === null ||
        normalizeCurveUnit(calibration.y_axis.unit) !== yUnit ||
        calibration.y_axis.scale !== yAxis.scale ||
        calibration.y_axis.direction !== yAxis.direction)
    ) {
      reasons.push(
        'The saved vertical calibration does not exactly match the AI axis quantity, unit, scale, and direction.',
      );
    }
  }

  if (
    document.selected_page !== chart.page_number ||
    document.selected_chart?.page_number !== chart.page_number
  ) {
    reasons.push(
      `Select and save chart page ${chart.page_number} in the manual editor before copying this curve.`,
    );
  }
  if (chart.region === null) {
    reasons.push(
      'The AI chart region is not identified, so chart mapping cannot be confirmed automatically.',
    );
  }
  if (document.selected_chart !== null) {
    const selectedLabel = document.selected_chart.label?.trim().toLocaleLowerCase();
    const suggestedLabel = chart.label?.trim().toLocaleLowerCase();
    if (!selectedLabel || !suggestedLabel) {
      reasons.push(
        'Both the saved chart and AI chart need labels before automatic mapping is safe.',
      );
    } else if (selectedLabel !== suggestedLabel) {
      reasons.push(
        `The saved chart label “${document.selected_chart.label}” does not match the AI chart label “${chart.label}”.`,
      );
    }
  }
  if (curve.label.trim() === '') reasons.push('The suggested curve has no label.');
  if (!Number.isFinite(curve.confidence) || curve.confidence < 0 || curve.confidence > 1) {
    reasons.push('The suggested confidence is invalid.');
  }
  if (
    curve.speed_rpm !== null &&
    (!Number.isInteger(curve.speed_rpm) || curve.speed_rpm <= 0)
  ) {
    reasons.push('The suggested RPM is invalid.');
  }
  if (curve.source_points.length < 2) {
    reasons.push('At least two source-unit points are required.');
  }
  if (curve.source_points.length > 500) {
    reasons.push('The suggestion exceeds the 500-point response contract.');
  }

  const identities = new Set<string>();
  const functional = curve.kind !== 'operating_boundary';
  for (const point of curve.source_points) {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      reasons.push('Every source-unit point must be finite.');
      break;
    }
    if (point.x < 0 || point.y < 0) {
      reasons.push('Source-unit points cannot be negative.');
      break;
    }
    const identity = `${point.x}:${point.y}`;
    if (identities.has(identity)) {
      reasons.push('The suggestion contains duplicate source-unit points.');
      break;
    }
    identities.add(identity);
    if (xAxis !== null && !within(point.x, xAxis)) {
      reasons.push('A point lies outside the suggested flow-axis range.');
      break;
    }
    if (yAxis !== null && !within(point.y, yAxis)) {
      reasons.push('A point lies outside the suggested vertical-axis range.');
      break;
    }
  }

  if (
    curve.source_points.some((point, index, points) => {
      const previous = points[index - 1];
      return previous !== undefined && previous.x > point.x;
    })
  ) {
    reasons.push('Source-unit points must be ordered by increasing flow.');
  }

  if (
    functional &&
    new Set(curve.source_points.map((point) => point.x)).size !==
      curve.source_points.length
  ) {
    reasons.push('A functional curve cannot contain repeated flow values.');
  }
  if (expectedY === 'efficiency' && yUnit !== null) {
    const maximum = yUnit === 'percent' ? 100 : 1;
    if (curve.source_points.some((point) => point.y > maximum)) {
      reasons.push('Suggested efficiency exceeds the supported unit range.');
    }
  }

  const auditMarker = `openai:${localRequestId}`;
  if (
    document.traces.some(
      (trace) =>
        trace.suggested_by === auditMarker &&
        trace.kind === curve.kind &&
        trace.label === curve.label,
    )
  ) {
    reasons.push('This audited suggestion is already present in the draft.');
  }

  return {
    compatible: reasons.length === 0,
    reasons: [...new Set(reasons)],
    xAxis,
    xUnit,
    yAxis,
    yUnit,
  };
}

export function buildAiTraceDraft(
  chart: AISuggestedChart,
  curve: AISuggestedCurve,
  document: CurveDraftDocument,
  localRequestId: string,
): CurveTraceDraft {
  const compatibility = analyzeAiCurveCompatibility(
    chart,
    curve,
    document,
    localRequestId,
  );
  if (!compatibility.compatible) {
    throw new Error(compatibility.reasons.join(' '));
  }
  if (document.selected_chart === null) {
    throw new Error('A saved chart selection is required.');
  }
  return {
    calibration_override: null,
    chart_region: document.selected_chart,
    contour_unit: null,
    contour_value: null,
    id: globalThis.crypto.randomUUID(),
    kind: curve.kind,
    label: curve.label.trim(),
    origin: 'measured',
    page_number: chart.page_number,
    points: curve.source_points.map((point, sequence) => ({
      id: globalThis.crypto.randomUUID(),
      pixel: null,
      sequence,
      source: { x: point.x, y: point.y },
    })),
    speed_rpm: curve.speed_rpm,
    suggested_by: `openai:${localRequestId}`,
    suggestion_confidence: curve.confidence,
    trim_label: curve.trim_label,
  };
}
